import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Modal } from '@arco-design/web-vue';
import Message from '@/utils/message';
// eslint-disable-next-line import/no-cycle
import { clearToken, getAccessToken, toSSO } from '@/utils/auth';
import { useUserStore } from '@/store';
import { ErrorCodeMap } from '@/libs/errorCode';
import useEdition from '@/hooks/useEdition';
import Cookies from 'js-cookie';
import router from '@/router';
import i18n from '@/locale';
import { iframeMessageManager } from '@/utils/iframeMessage';
import { ssoLoginDirect } from './sso';

export interface HttpResponse<T = unknown> {
  status: number;
  msg: string;
  code: number;
  data: T;
  errorcode?: number;
  success?: boolean;
}

// 存待重放的请求
type PendingReq = {
  resolve: (value: any) => void;
  reject: (err: any) => void;
  config: AxiosRequestConfig;
};
let pendingQueue: PendingReq[] = [];
let refreshPromise: Promise<string | undefined> | null = null;
// 防止短时间内重复刷新 token
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 5000; // 5秒冷却期
// 跟踪正在重放的请求，防止循环刷新
const retryingRequests = new Set<string>();

axios.defaults.timeout = 600000;

const silentRefreshViaIframe = async (): Promise<string | null> => {
  const iframe = document.getElementById(
    'sso-iframe'
  ) as HTMLIFrameElement | null;
  if (!iframe?.contentWindow) return null;
  try {
    // 清除旧 code，防止使用过期消息
    iframeMessageManager.clearMessage('sso_code');
    iframe.contentWindow.postMessage(
      { key: 'get_code', value: 'true' },
      import.meta.env.VITE_SSO_URL as string
    );
    const msg = await iframeMessageManager.waitForMessage('sso_code', 3000);
    if (!msg?.value) return null;
    const res = await ssoLoginDirect(msg.value);
    const token = res.data?.data?.access_token;
    if (res.data?.code === 0 && token) {
      iframeMessageManager.clearMessage('sso_code');
      return token;
    }
    return null;
  } catch {
    return null;
  }
};

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const { isApiDisabled, edition: currentEdition } = useEdition();
    if (isApiDisabled(config.url)) {
      Message.warning(i18n.global.t('messages.featureNotSupportedInEdition'));
      return Promise.reject(
        new Error(`Api disabled in current edition: ${currentEdition}`)
      );
    }
    // let each request carry token
    // this example using the JWT token
    // Authorization is a custom headers key
    // please modify it according to the actual situation
    if (!config.headers) {
      config.headers = {};
    }
    // 如果已经有 Authorization header（比如在 flushQueue 中设置的），就不覆盖
    if (!config.headers.Authorization) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // 添加 debug settings 中的 headers（仅在开发环境）
    if (import.meta.env.DEV) {
      try {
        const workflowHttpBaseUrl = localStorage.getItem(
          'workflow-http-base-url'
        );
        if (workflowHttpBaseUrl) {
          config.headers['workflow-http-base-url'] = workflowHttpBaseUrl;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[axios] 读取 debug settings 失败:', error);
      }
    }

    config.headers['Knit-User-Locale'] =
      (i18n.global.locale as any) ||
      localStorage.getItem('knit-locale') ||
      'en-US';

    const userStore = useUserStore();

    // 插入：部分接口不需要添加 tenantUuid 和 workspaceUuid
    // 支持通配符*，判断是否跳过
    const needAddTenantWorkspace = ['/api/console/workflow/*'];
    // 判断是否匹配通配符（仅支持结尾*，即前缀匹配）
    const isNeedAddTenantWorkspace = (url?: string) => {
      if (!url) return false;
      return needAddTenantWorkspace.some((pattern) => {
        if (pattern.endsWith('*')) {
          const prefix = pattern.slice(0, -1);
          return url.startsWith(prefix);
        }
        return url === pattern;
      });
    };

    // 自动添加 tenant_uuid 和 workspace_uuid
    const { tenantUuid, workspaceUuid } = userStore;
    if (tenantUuid && workspaceUuid && isNeedAddTenantWorkspace(config.url)) {
      if (config.method === 'get') {
        config.params = {
          tenant_uuid: tenantUuid,
          workspace_uuid: workspaceUuid,
          ...config.params,
        };
      } else if (
        ['post', 'put', 'patch', 'delete'].includes(config.method || '')
      ) {
        if (config.data instanceof FormData) {
          if (!config.data.has('tenant_uuid'))
            config.data.append('tenant_uuid', tenantUuid);
          if (!config.data.has('workspace_uuid'))
            config.data.append('workspace_uuid', workspaceUuid);
        } else {
          const data = config.data || {};
          if (typeof data === 'object' && !Array.isArray(data)) {
            if (!data.tenant_uuid) data.tenant_uuid = tenantUuid;
            if (!data.workspace_uuid) data.workspace_uuid = workspaceUuid;
            config.data = data;
          }
        }
      }
    }
    // eslint-disable-next-line no-console
    console.log('[axios] 请求:', config.method, config.baseURL, config.url);
    return config;
  },
  (error) => {
    // do something
    return Promise.reject(error);
  }
);
// add response interceptors
axios.interceptors.response.use(
  (response: AxiosResponse<HttpResponse> | any) => {
    // eslint-disable-next-line no-console
    console.log(
      '[axios] 响应:',
      response.status,
      response.config.url,
      response.data
    );
    // 特殊接口直接返回原始数据
    if (
      /^\/api\/kb\/\d+\/document\/\d+\/content$/.test(response.config.url) &&
      response.status === 200
    ) {
      return response.data;
    }
    if (['/version.json', '/changelog.json'].includes(response.config.url)) {
      return response.data;
    }

    // 统一返回 HttpResponse 格式
    const res: HttpResponse = response.data;
    if (response.config.url?.includes('export')) {
      return res;
    }
    // if the custom code is not 20000, it is judged as an error.
    if (
      res.code === 200 ||
      res.success ||
      res.code === 0 ||
      res.code === 20001
    ) {
      return res;
    }
    Message.error({
      content: ErrorCodeMap[(res.data as any)?.code] || res.msg || 'Error',
      duration: 5 * 1000,
    });
    if (res.code === 401) {
      // 清除 token 和用户信息，防止用户忽略弹窗后仍能访问
      clearToken();
      const userStore = useUserStore();
      userStore.resetInfo();
      Modal.error({
        title: i18n.global.t('messages.confirmLogout'),
        content: i18n.global.t('messages.loginExpired'),
        okText: i18n.global.t('messages.reLogin'),
        async onOk() {
          if (import.meta.env.VITE_LOGIN_TYPE === 'internal') {
            router.push('/login');
          } else {
            toSSO();
          }
        },
      });
    }
    return Promise.reject(new Error(res.msg || 'Error'));
  },
  (error) => {
    // eslint-disable-next-line no-console
    console.error(
      '[axios] 错误:',
      error?.response?.status,
      error?.config?.url,
      error?.response?.data,
      error
    );
    // 处理请求取消的情况
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // 将 401 的原请求挂起，等待刷新完成后重放
    const enqueueRequest = (cfg: AxiosRequestConfig) => {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject, config: cfg });
      });
    };

    // 生成请求的唯一标识
    const getRequestId = (cfg: AxiosRequestConfig) => {
      return `${cfg.method || 'GET'}:${cfg.url || ''}`;
    };

    // 刷新成功后，带上新 token 重放所有挂起请求
    const flushQueue = (token?: string) => {
      const queueToFlush = [...pendingQueue];
      pendingQueue = [];

      queueToFlush.forEach(({ resolve, reject, config: originalConfig }) => {
        // 深拷贝 config，避免修改原始配置
        const cfg: AxiosRequestConfig = {
          ...originalConfig,
          headers: originalConfig.headers ? { ...originalConfig.headers } : {},
        };
        // 设置新 token，请求拦截器会检查如果已有 Authorization 就不覆盖
        if (token) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${token}`;
        }
        // 标记为重放请求，防止再次触发刷新流程
        const requestId = getRequestId(cfg);
        retryingRequests.add(requestId);

        axios(cfg)
          .then((response) => {
            retryingRequests.delete(requestId);
            resolve(response);
          })
          .catch((err) => {
            retryingRequests.delete(requestId);
            // 如果重放请求再次返回 401，不再触发刷新，直接拒绝
            if (err?.response?.status === 401) {
              // eslint-disable-next-line no-console
              console.warn('[axios] 重放请求再次返回 401，不再重试:', cfg.url);
            }
            reject(err);
          });
      });
    };

    // 刷新失败时，拒绝所有挂起请求
    const failQueue = (err: any) => {
      pendingQueue.forEach(({ reject }) => reject(err));
      pendingQueue = [];
    };

    const { response, config } = error;

    // 账号被禁用：HTTP 400 + code 10009
    if (response?.status === 403 && response?.data?.code === 10009) {
      clearToken();
      const userStore = useUserStore();
      userStore.resetInfo();
      router.push('/403');
      return Promise.reject(error);
    }

    if (response?.status === 401) {
      // 如果是重放请求再次返回 401，不再触发刷新，直接拒绝
      const requestId = getRequestId(config as AxiosRequestConfig);
      if (retryingRequests.has(requestId)) {
        // eslint-disable-next-line no-console
        console.warn('[axios] 重放请求返回 401，不再重试:', config?.url);
        return Promise.reject(error);
      }

      // 检查冷却期，防止短时间内重复刷新
      const now = Date.now();
      if (now - lastRefreshTime < REFRESH_COOLDOWN && refreshPromise) {
        // 在冷却期内，直接排队等待
        return enqueueRequest(config as AxiosRequestConfig);
      }

      // 已有刷新在进行，直接排队等待
      if (!refreshPromise) {
        // 仅触发一次刷新，其他 401 请求排队
        lastRefreshTime = now;
        refreshPromise = (async () => {
          try {
            let newToken: string | null = null;

            if (import.meta.env.VITE_LOGIN_TYPE !== 'internal') {
              // SSO 模式：走 iframe 静默刷新
              newToken = await silentRefreshViaIframe();
            }

            if (newToken) {
              Cookies.set('access_token', newToken);
              flushQueue(newToken);
              return newToken;
            }

            // 刷新失败 — 清除 session，弹窗提示
            clearToken();
            const userStore = useUserStore();
            userStore.resetInfo();
            failQueue(new Error(i18n.global.t('messages.refreshTokenFailed')));
            Modal.error({
              title: i18n.global.t('messages.confirmLogout'),
              content: i18n.global.t('messages.loginExpired'),
              okText: i18n.global.t('messages.reLogin'),
              async onOk() {
                if (import.meta.env.VITE_LOGIN_TYPE === 'internal') {
                  router.push('/login');
                } else {
                  toSSO();
                }
              },
            });
            throw new Error(i18n.global.t('messages.refreshTokenFailed'));
          } finally {
            refreshPromise = null;
          }
        })();
      }

      return enqueueRequest(config as AxiosRequestConfig);
    }
    Message.error({
      content:
        ErrorCodeMap[error?.response?.data?.data?.code] ||
        error?.response?.data?.msg ||
        'Request Error',
      duration: 5 * 1000,
    });
    return Promise.reject(error);
  }
);
